import { Button } from '@/components/button';
import { useEncounter } from '@/contexts/encounter-context';
import { MAX_IDV_RETRIES } from '@/util/constants';
import { useGetEncountersIdentityVerificationInfo } from '@/api/get-verification-info';
import { useTranslation } from '@/contexts/i18n-context';
import type { TranslationKey } from '@/contexts/translations';
import styles from './rejection-page.module.css';
import { SimpleVisitPageContainer } from '@/components/simple-visit-page-container';
import { Navigate, useNavigate } from '@/util/react-router-dom';
import { IdentityVerificationStatus } from '@/api/api.schemas';
import { useGetEncountersIdentityVerificationErrors } from '@/api/get-verification-errors';
import { useCreateEncountersIdentityVerificationManualRequest } from '@/api/create-manual-request';
import { useUpdatePatientEncountersBirthday } from '@/api/update-encounter-birthday';
import { useLoggedUser } from '@/contexts/current-user-context';
import { CancelSvg } from '@/components/svgs/cancel';
import { Loader } from '@/components/loaders/loader';
import { Banner } from '@/components/banner';
import { useBranding } from '@/outlets/branding-outlet';

const translationBase = 'visit.identityVerification.rejection';

export function IdvRejectionPage() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const { user } = useLoggedUser();
  const navigate = useNavigate();
  const { readonlyProfile, name } = useBranding();

  const { data: verificationInfo, isFetching: isFetchingVerificationInfo } =
    useGetEncountersIdentityVerificationInfo(encounter.id);
  const { data: identityVerificationErrors, isLoading: isLoadingIdentityVerificationErrors } =
    useGetEncountersIdentityVerificationErrors(encounter.id);
  const { mutate: createEncountersManualRequest, isPending: isPendingManualRequest } =
    useCreateEncountersIdentityVerificationManualRequest({
      mutation: {
        onSuccess: () => {
          navigate(`/visit/${encounter.id}/identity-verification/manual-request`);
        },
      },
    });
  const { mutate: updatePatientEncountersBirthday, isPending: isUpdatingEncounterBirthday } =
    useUpdatePatientEncountersBirthday({
      mutation: {
        onSuccess: () => navigate(`/visit/${encounter.id}/identity-verification`),
        onError: () => navigate('/system-error'),
      },
    });

  if (isLoadingIdentityVerificationErrors || isFetchingVerificationInfo) {
    return <Loader type="large" />;
  }

  if (verificationInfo?.manualReviewStatus === IdentityVerificationStatus.ManualReview) {
    return <Navigate to="/" />;
  }

  const hasAttemptsLeft =
    verificationInfo?.currentRetries !== undefined &&
    verificationInfo.currentRetries < MAX_IDV_RETRIES;

  const birthDateMismatch = identityVerificationErrors?.birthDateMismatch;

  const getErrorTitle = (errorType: string) => {
    if (errorType === 'BirthDateMatchError' && birthDateMismatch) {
      return t(`${translationBase}.errors.BirthDateMatchError.title`, {
        idBirthDate: birthDateMismatch.idBirthDate,
        submittedBirthDate: birthDateMismatch.submittedBirthDate,
      });
    }

    return t(`${translationBase}.errors.${errorType}.title` as TranslationKey);
  };

  return (
    <SimpleVisitPageContainer
      icon={<CancelSvg className={styles.cancelIcon} ariaLabel={t(`${translationBase}.alt`)} />}
      title={
        hasAttemptsLeft ? t(`${translationBase}.titleRetry`) : t(`${translationBase}.titleManual`)
      }
      body={
        hasAttemptsLeft ? (
          <>
            {identityVerificationErrors?.errors &&
              identityVerificationErrors?.errors.length > 0 && (
                <ul className={styles.errorList}>
                  {identityVerificationErrors.errors.map(
                    (error) =>
                      error.type && (
                        <li key={error.type}>
                          <p className="bold">{getErrorTitle(error.type)}</p>
                          <p className={styles.errorDescription}>
                            {t(`${translationBase}.errors.${error.type}.description`)}
                          </p>
                        </li>
                      ),
                  )}
                </ul>
              )}
            {birthDateMismatch && readonlyProfile && (
              <Banner
                type="warning"
                className={styles.updatingExternalNetworkBanner}
                labelClassName={styles.updatingExternalNetworkLabel}
                label={t(`${translationBase}.updatingBirthdayOnExternalNetwork`, {
                  network: name ?? t(`${translationBase}.yourPortal`),
                })}
              />
            )}
          </>
        ) : (
          <p>{t(`${translationBase}.descriptionManual`)}</p>
        )
      }
      buttonContainer={
        <div className={styles.buttonContainer}>
          {hasAttemptsLeft && (
            <Button as="a" to={`/visit/${encounter.id}/identity-verification`} fullWidth>
              {t(`${translationBase}.tryAgain`)}
            </Button>
          )}
          {hasAttemptsLeft && birthDateMismatch ? (
            <Button
              variant="outlined"
              fullWidth
              loading={isUpdatingEncounterBirthday}
              onClick={() =>
                updatePatientEncountersBirthday({ patientId: user.id, encounterId: encounter.id })
              }
            >
              {t(`${translationBase}.updateProfileAndRedo`)}
            </Button>
          ) : (
            <Button
              variant={hasAttemptsLeft ? 'outlined' : 'filled'}
              fullWidth
              loading={isPendingManualRequest}
              onClick={() => createEncountersManualRequest({ encounterId: encounter.id })}
            >
              {t(`${translationBase}.manualReview`)}
            </Button>
          )}
          {!hasAttemptsLeft && (
            <Button variant="outlined" as="a" to="/" fullWidth>
              {t(`${translationBase}.backToDashboard`)}
            </Button>
          )}
          {!(hasAttemptsLeft && birthDateMismatch) && (
            <span className={styles.availability}>
              {t(`${translationBase}.supportAvailability`, {
                availability: (
                  <span key="availability" className="bold">
                    {t(`${translationBase}.availability`)}
                  </span>
                ),
              })}
            </span>
          )}
        </div>
      }
    />
  );
}
